import type { Citation, Presentation } from '../types';
import { safeUrl } from '../safeUrl';

interface InfoPanelProps {
  /** Tools that the orchestrator dispatched on the current turn. Empty until first tool_call event. */
  toolsUsed: string[];
  /** Currently in-flight tool keys (subset of toolsUsed). */
  toolsInFlight: string[];
  /** Citations as of the latest presentation event; empty until the stream finishes. */
  citations: Citation[];
  /** The presentation envelope chosen for this turn, or null pre-presentation. */
  presentation: Presentation | null;
}

// Right-side panel shown on the asking state at the lg+ breakpoint. Three
// sections per DESIGN §5.3: active tools, sources cited, presentation type.
export function InfoPanel({
  toolsUsed,
  toolsInFlight,
  citations,
  presentation,
}: InfoPanelProps) {
  const inFlight = new Set(toolsInFlight);

  return (
    <aside className="info-panel info-panel--asking" aria-label="Trace and sources">
      <section className="info-panel-section">
        <h2 className="info-panel-heading">Active tools</h2>
        {toolsUsed.length === 0 ? (
          <p className="info-panel-muted">Selecting tools…</p>
        ) : (
          <ul className="info-panel-tools">
            {toolsUsed.map((t) => (
              <li key={t} className={inFlight.has(t) ? 'tool-row tool-row--active' : 'tool-row tool-row--done'}>
                <span className="tool-row-dot" aria-hidden="true">{inFlight.has(t) ? '◌' : '●'}</span>
                <span className="tool-row-name">{t}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="info-panel-section">
        <h2 className="info-panel-heading">Sources cited</h2>
        {citations.length === 0 ? (
          <p className="info-panel-muted">Citations will appear after the answer streams.</p>
        ) : (
          <ol className="info-panel-citations">
            {citations.map((c) => (
              <li key={c.index}>
                <a href={safeUrl(c.url)} target="_blank" rel="noopener noreferrer">
                  {c.label}
                </a>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="info-panel-section">
        <h2 className="info-panel-heading">Presentation</h2>
        <div className="info-panel-pres-pill">
          {presentation ? presentation.type : 'pending…'}
        </div>
      </section>
    </aside>
  );
}
